import { useMemo, useState } from 'react'

import { joiResolver } from '@hookform/resolvers/joi'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Dialog, DialogActions, DialogContent, Grid, Typography } from '@mui/material'
import Joi from 'joi'
import { useForm } from 'react-hook-form'

import {
  ControlledSelector,
  ControlledTextField,
  FplDialogTitle,
  ISelectorOptions,
  LoadingBackdrop,
} from 'components/common'
import {
  CreateBidAllocationDtoInput,
  useGetDriverByCurrentUserQuery,
  useGetAvailableSubDriversQuery,
  useGetOwnersVehiclesQuery,
} from 'generated/graphql'

import AllocateConfirmDialog from './AllocateConfirmDialog'

interface ISupplierAllocateDialogProps {
  openDialog: boolean
  onCloseDialog: () => void
  onAllocateDriver: (formValue: CreateBidAllocationDtoInput) => void
  loading: boolean
  bookingId: number
}

const labelId = 'add-driver-allocation-dialog-title'

export const SupplierAllocateDialog = (props: ISupplierAllocateDialogProps) => {
  const { openDialog, onCloseDialog, bookingId, onAllocateDriver, loading } = props

  const [openConfirmModal, setOpenConfirmModal] = useState(false)

  const allocationSchema = Joi.object({
    driverId: Joi.string().required().messages({ 'any.required': 'Driver is required' }),
    bookingId: Joi.string().required(),
    vehicleId: Joi.string().required().messages({ 'any.required': 'Vehicle is required' }),
  })

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<CreateBidAllocationDtoInput>({
    shouldUnregister: true,
    defaultValues: {
      driverId: '',
      bookingId: bookingId.toString(),
      vehicleId: '',
    },
    resolver: joiResolver(allocationSchema),
  })

  const { data: driverData } = useGetDriverByCurrentUserQuery()

  const { loading: availableDriversLoading, data: availableDriversData } =
    useGetAvailableSubDriversQuery({
      variables: {
        bookingId: bookingId,
      },
      fetchPolicy: 'no-cache',
    })

  const { loading: vehiclesLoading, data: vehiclesData } = useGetOwnersVehiclesQuery({
    onCompleted: (response) => {
      const defaultVehicleId = response.ownersVehicles.filter(
        (v) => v.id === driverData?.driverByCurrentUser?.activeVehicleId,
      )[0]?.id
      if (defaultVehicleId) {
        setValue('vehicleId', defaultVehicleId)
      }
    },
  })

  const availableDrivers: ISelectorOptions = useMemo(() => {
    if (availableDriversData?.availableSubDrivers) {
      return availableDriversData.availableSubDrivers.map((driver) => {
        const isCurrentUser = driver.id === driverData?.driverByCurrentUser?.id
        const displayName = isCurrentUser ? 'You' : driver.name
        return {
          id: driver.id,
          label: driver.isActive ? displayName : `${displayName} (Blocked)`,
          value: driver.id,
        }
      })
    }

    return []
  }, [availableDriversData, driverData?.driverByCurrentUser?.id])

  const vehicles: ISelectorOptions = useMemo(() => {
    if (vehiclesData?.ownersVehicles) {
      return vehiclesData.ownersVehicles.map((vehicle) => ({
        id: vehicle.id,
        label: `${vehicle.vehicleType.name} - ${vehicle.registrationNumber} `,
        value: vehicle.id,
      }))
    }

    return []
  }, [vehiclesData])

  const handleApprove = () => {
    const values = getValues()
    const isSelfAllocation = values.driverId === driverData?.driverByCurrentUser?.id

    onAllocateDriver({
      ...values,
      isSelfAllocation,
    })
    setOpenConfirmModal(false)
  }

  const handleConfirmDialog = () => {
    setOpenConfirmModal(true)
  }

  return (
    <Dialog fullWidth maxWidth='sm' aria-labelledby={labelId} open={openDialog}>
      <FplDialogTitle id={labelId} onClose={onCloseDialog}>
        <Typography mb={0} paragraph variant='h4'>
          Select driver for allocation
        </Typography>
      </FplDialogTitle>

      <DialogContent>
        <Box pt='6px'>
          <Grid container spacing={1.5}>
            <Box hidden>
              <ControlledTextField control={control} name='bookingId' label='' />
            </Box>

            <Grid item xs={12}>
              <ControlledSelector
                control={control}
                name='driverId'
                options={availableDrivers}
                label='Driver'
                required
                error={!!errors.driverId}
                helperText={errors.driverId?.message}
              />
            </Grid>

            <Grid item xs={12}>
              <ControlledSelector
                control={control}
                name='vehicleId'
                options={vehicles}
                label='Vehicle'
                required
                error={!!errors.vehicleId}
                helperText={errors.vehicleId?.message}
              />
            </Grid>
          </Grid>
        </Box>

        <LoadingBackdrop loading={loading || availableDriversLoading || vehiclesLoading} />
      </DialogContent>

      <DialogActions>
        <Button variant='contained' color='grey' onClick={onCloseDialog}>
          Cancel
        </Button>

        <LoadingButton
          loading={loading || availableDriversLoading || vehiclesLoading}
          variant='contained'
          disabled={!isDirty || !isValid}
          onClick={handleSubmit(handleConfirmDialog)}>
          Allocate
        </LoadingButton>
      </DialogActions>

      <AllocateConfirmDialog
        bookingId={bookingId}
        control={control}
        openConfirmModal={openConfirmModal}
        setOpenConfirmModal={setOpenConfirmModal}
        onConfirm={handleApprove}
      />
    </Dialog>
  )
}
